<script setup>
import {ref, computed, watch} from 'vue'
import {useRouter} from 'vue-router'
import {useFormsStore} from '@/stores/forms'
import {useComponentFieldsStore} from '@/stores/componentFields'
import {useModalsStore} from '@/stores/modals'
import {useItemDataStore} from '@/stores/itemData'
import {useFormStore} from '@/stores/form'
import Form from '@/common/form/Form'
import FormSaveBtn from '@/common/form/FormSaveBtn'
import Button from '@/common/Button.vue'

const props = defineProps({
    modal: Object
})

const router = useRouter()
const tableName = 'modulestructure'

const formsStore = useFormsStore()
const componentFieldsStore = useComponentFieldsStore()
const modalsStore = useModalsStore()
const itemDataStore = useItemDataStore()

// --- State ---

const activeEditId = computed(() => componentFieldsStore.activeEditId)
const componentId = computed(() => componentFieldsStore.componentId)
const items = computed(() => componentFieldsStore.items)

const item = ref(null)

// Native fetch logic replacing useAsyncState
const fetchItem = async () => {
    if (activeEditId.value === undefined || activeEditId.value === null) {
        item.value = null
        return
    }

    item.value = await itemDataStore.get({
        tableName: tableName,
        id: activeEditId.value
    })
}

// --- Computed ---

const formId = computed(() => {
    if (item.value) {
        const id = item.value.id || 0
        return `form:${tableName}:${id}`
    }
    return null
})

// Dynamic Pinia Form Store Helper
const activeFormStore = computed(() => formId.value ? useFormStore(formId.value) : null)

const titleValue = computed(() => {
    if (activeFormStore.value) {
        return activeFormStore.value.currentData?.title
    }
    return null
})

// --- Methods ---

const createForm = () => {
    if (!item.value || !formId.value) return

    const fieldNames = componentFieldsStore.getFields(item.value.type)

    if (props.modal) {
        props.modal.formId = formId.value
    }

    formsStore.createForm({
        tableName: tableName,
        id: activeEditId.value,
        fieldNames: fieldNames,
        onInit: () => {
            const formStore = useFormStore(formId.value)
            const fields = formStore.fields
            const field = fields.find(f => f.columnName === 'title')
            if (field) {
                field.label = item.value.type === 'fieldset' ? 'Legend' : 'Label'
            }

            if (activeEditId.value === 0) {
                const presets = [
                    {name: 'modulecomponentsID', value: componentId.value}, // todo Deprecated
                    {name: 'modulecomponentsId', value: componentId.value},
                    {name: 'browser', value: 1}
                ]

                if (componentFieldsStore.activeFieldsetId) {
                    presets.push({
                        name: 'fieldsetId',
                        value: componentFieldsStore.activeFieldsetId
                    })
                }

                presets.forEach(p => {
                    formStore.presetData(p)
                })
            }
        }
    })

    // Provide modifiedData getter to the service
    componentFieldsStore.getModifiedData = () => {
        const formStore = useFormStore(formId.value)
        return formStore.modifiedData
    }
}

const confirmDelete = (e, itemObj) => {
    e.stopPropagation()
    modalsStore.show({
        componentName: 'ConfirmModal',
        obj: {
            modalTitle: 'Are you sure?',
            modalBody: `
                <div class="alert alert-danger">This action will result in loss of data.</div>
                <p>This action <strong>cannot</strong> be undone. This will permanently delete all data from the <strong>${itemObj.title}</strong> field.</p>
            `,
            confirmStr: itemObj.title,
            onConfirm: () => {
                componentFieldsStore.deleteField({
                    fieldId: itemObj.id,
                    router: router
                })
            }
        }
    })
}

// --- Watchers ---

// Fetch item when activeEditId changes
watch(activeEditId, () => fetchItem(), {immediate: true})

// Create the form once formId is established or changed
watch(formId, (newId) => {
    if (newId) createForm()
}, {immediate: true})

// Sync display order when item list changes
watch(items, (newItems) => {
    if (activeFormStore.value) {
        activeFormStore.value.presetData({
            name: 'displayOrder',
            value: newItems.length + 1,
        })
    }
}, {immediate: true})

// Validation logic for the 'title' field
watch(titleValue, (val) => {
    if (!activeFormStore.value) return

    activeFormStore.value.setFieldProperty({
        fieldName: 'title',
        property: 'error',
        value: ''
    })

    if (val) {
        const el = document.querySelector('[name="title"]')
        let hasError = false

        items.value.forEach((i) => {
            if (i.id !== activeEditId.value && i.title === val) {
                hasError = true
                el?.setCustomValidity('Label in use.')
                el?.reportValidity()
            }
        })

        if (!hasError && el) {
            el.setCustomValidity('')
            el.reportValidity()
        }
    }
})
</script>

<template>
    <div v-if="item">
        <Form v-if="formId" :form-id="formId" :show-legends="true"/>

        <div class="clearfix text-center mt-3">
            <FormSaveBtn :button-text="'Save'" :form-id="formId"/>

            <Button v-if="item.id" @click="confirmDelete($event, item)">
                Delete
            </Button>
        </div>
    </div>
</template>